import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "code-cell" }
const _hoisted_2 = { class: "code-cell-grid" }
const _hoisted_3 = { class: "state-info" }
const _hoisted_4 = {
  key: 0,
  class: "pi pi-spin pi-spinner busy-icon"
}

import { ref, shallowRef, computed, inject, getCurrentInstance, onBeforeMount, onBeforeUnmount, nextTick } from "vue";
import CodeCellOutput from "./BeakerCodeCellOutput.vue";
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import { findSelectableParent } from "../../util";
import { BeakerSession } from "beaker-kernel/src";
import CodeEditor from "../misc/CodeEditor.vue";
import { type BeakerSessionComponentType } from '../session/BeakerSession.vue';
import { type BeakerNotebookComponentType } from '../notebook/BeakerNotebook.vue';
import { IBeakerTheme } from '../../plugins/theme';


import { BeakerCodeCell } from "beaker-kernel/src";
const __default__ = {
    modelClass: BeakerCodeCell,
    icon: "pi pi-code",
};


export default /*@__PURE__*/_defineComponent({
  ...__default__,
  __name: 'BeakerCodeCell',
  props: [
    "cell",
    "hideOutput"
],
  emits: [
    'blur',
],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const cell = ref(props.cell);
const { theme } = inject<IBeakerTheme>('theme');
const session = inject<BeakerSession>('session');
const codeEditorRef = ref<InstanceType<typeof CodeEditor>>();
const beakerSession = inject<BeakerSessionComponentType>("beakerSession");
const notebook = inject<BeakerNotebookComponentType>("notebook");
const instance = getCurrentInstance();

const emit = __emit

enum ExecuteStatus {
  Success = 'ok',
  Modified = 'modified',
  Error = 'error',
  Pending = 'pending',
  None = 'none'
}

const hasRollback = computed(() => {
    return typeof(cell.value?.last_execution?.checkpoint_index) !== "undefined";
});

const rollback = () => cell.value.rollback(session);

const isBusy = computed(() => {
    return cell.value?.busy;
});

const badgeSeverity = computed(() => {
    const defaultValue = "secondary";
    const mappings = {
        [ExecuteStatus.Success]: 'success',
        [ExecuteStatus.Modified]: 'warning',
        [ExecuteStatus.Error]: 'danger',
        [ExecuteStatus.Pending]: defaultValue,
        [ExecuteStatus.None]: defaultValue,
    };
    return mappings[cell.value?.last_execution?.status] || defaultValue;
});

const clicked = (evt) => {
    if (notebook) {
        notebook.selectCell(cell.value);
    }
    evt.stopPropagation();
};

function handleCodeChange() {
    cell.value.reset_execution_state();
}

const language = computed(() => (beakerSession.activeContext?.language?.slug || undefined));

const execute = (evt: any) => {
    const future = props.cell.execute(session);
    exit();
}

const enter = () => {
    codeEditorRef.value?.focus();
}

const exit = () => {
    // Be sure to blur editor even if we don't also refocus below.
    codeEditorRef.value?.blur();
    let target: HTMLElement = (instance.vnode.el as HTMLElement);
    const selectableParent = findSelectableParent(target);
    selectableParent?.focus();
}

const clear = () => {
    cell.value.source = "";
    cell.value.outputs.splice(0, cell.value.outputs.length);
}

__expose({
    execute,
    enter,
    exit,
    clear,
    cell,
});

onBeforeMount(() => {
    beakerSession.cellRegistry[cell.value.id] = instance.vnode;
});

onBeforeUnmount(() => {
    delete beakerSession.cellRegistry[cell.value.id];
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: _normalizeClass(["code-data", {'dark-mode': _unref(theme).mode === 'dark'}])
      }, [
        _createVNode(CodeEditor, {
          "display-mode": "dark",
          language: language.value,
          modelValue: cell.value.source,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((cell.value.source) = $event)),
          ref_key: "codeEditorRef",
          ref: codeEditorRef,
          placeholder: "Your code...",
          disabled: isBusy.value,
          onChange: handleCodeChange,
          onClick: clicked
        }, null, 8, ["language", "modelValue", "disabled"]),
        _withDirectives(_createVNode(CodeCellOutput, {
          outputs: cell.value.outputs,
          busy: isBusy.value
        }, null, 8, ["outputs", "busy"]), [
          [_vShow, !__props.hideOutput]
        ])
      ], 2),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", null, [
          _createVNode(_unref(Badge), {
            class: _normalizeClass(["execution-count-badge", {secondary: badgeSeverity.value === 'secondary'}]),
            severity: badgeSeverity.value,
            value: cell.value.execution_count || ' '
          }, null, 8, ["class", "severity", "value"])
        ]),
        (isBusy.value)
          ? (_openBlock(), _createElementBlock("i", _hoisted_4))
          : _createCommentVNode("", true),
        (hasRollback.value)
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 1,
              class: "rollback-button",
              severity: badgeSeverity.value,
              icon: "pi pi-undo",
              size: "small",
              onClick: rollback
            }, null, 8, ["severity"]))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})